// Sopwith 3D - sprite ("symbol") decoding.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Sprites are stored in mission files as ASCII art, two characters per pixel
// (the second character of each pair is ignored). The characters ' ', '*',
// '-' and '#' map to colour indices 0..3, where 0 is transparent. Colour data
// is what collision detection tests, so the sim needs it even before there is
// a renderer.

import { SYM_HGHT, SYM_WDTH } from './constants';
import type { Sopsym, Symset } from './types';

const COLOR_CHARS = ' *-#';

function symbolDimensions(text: string): { w: number; h: number } {
  let w = 0;
  let h = 0;
  let p = 0;
  while (p < text.length) {
    const lineStart = p;
    while (p < text.length && text[p] !== '\n') {
      p++;
    }
    const lineEnd = p;
    if (p >= text.length && lineStart === lineEnd) {
      break;
    }
    w = Math.max(w, ((lineEnd - lineStart + 1) / 2) | 0);
    h++;
    p++;
  }
  return { w, h };
}

/** Rotate a pixel coordinate `rotations` quarter turns CCW, then mirror it. */
function rotate(
  x: number,
  y: number,
  w: number,
  h: number,
  rotations: number,
  mirror: boolean,
): { x: number; y: number } {
  for (let i = 0; i < rotations; i++) {
    const tmp = x;
    x = y;
    y = w - 1 - tmp;
    const t2 = w;
    w = h;
    h = t2;
  }
  if (mirror) {
    y = h - 1 - y;
  }
  return { x, y };
}

/** Decode one ASCII-art sprite into a Sopsym, applying a transform. */
export function sopsymFromText(text: string, rotations: number, mirror: boolean): Sopsym {
  const { w, h } = symbolDimensions(text);
  const sym: Sopsym =
    (rotations & 1) === 0
      ? { w, h, data: new Uint8Array(w * h) }
      : { w: h, h: w, data: new Uint8Array(w * h) };

  let x = 0;
  let y = 0;
  for (let p = 0; p < text.length; p++) {
    const ch = text[p];
    if (ch === '\n') {
      x = 0;
      y++;
      continue;
    }
    const idx = COLOR_CHARS.indexOf(ch);
    const c = idx >= 0 ? idx : 0;
    if (x < w * 2 && y < h && (x & 1) === 0) {
      const r = rotate(x >> 1, y, w, h, rotations, mirror);
      sym.data[r.y * sym.w + r.x] = c;
    }
    x++;
  }
  return sym;
}

/** Decode a sprite in all eight transforms (index = Transform value). */
export function symsetFromText(text: string): Symset {
  const sym: Sopsym[] = [];
  for (let r = 0; r < 4; r++) {
    sym[r] = sopsymFromText(text, r, false);
    sym[r + 4] = sopsymFromText(text, r, true);
  }
  return { sym };
}

/** The 1x1 sprite used for bullets. */
export const SYMBOL_PIXEL: Sopsym = { w: 1, h: 1, data: new Uint8Array([3]) };

/** Names of the symbol sets a mission file may define, with frame counts. */
export const SYMBOL_SET_NAMES: Record<string, number> = {
  swbmbsym: 2, // bombs
  swtrgsym: 10, // ground targets
  swhtrsym: 10, // ground targets, hit
  swexpsym: 8, // explosion debris
  swflksym: 2, // bird flock
  swbrdsym: 2, // single bird
  swoxsym: 2, // ox
  swshtsym: 1, // shot-out windscreen
  swsplsym: 1, // bird splat
  swmscsym: 4, // missile
  swbstsym: 2, // starburst
  swplnsym: 4, // plane
  swhitsym: 4, // plane, hit
  swwinsym: 4, // plane flying into the sunset
  swmedalsym: 3,
  swribbonsym: 6,
  swballoonsym: 6,
  swpowerupsym: 6,
  swpowercollsym: 6,
};

/** All decoded symbol sets, keyed by set name then frame. */
export type SymbolTable = Map<string, Symset[]>;

/** Build a symbol table from `name -> frame texts`. Unknown names are kept. */
export function buildSymbolTable(texts: Map<string, string[]>): SymbolTable {
  const table: SymbolTable = new Map();
  for (const [name, frames] of texts) {
    table.set(
      name,
      frames.map((t) => symsetFromText(t)),
    );
  }
  return table;
}

/** Look up a frame, throwing a clear error if the mission lacks it. */
export function getSymset(table: SymbolTable, name: string, frame: number): Symset {
  const set = table.get(name);
  const s = set?.[frame];
  if (!s) {
    throw new Error(`missing symbol ${name}[${frame}]`);
  }
  return s;
}

export const DEFAULT_SYMBOL_SIZE = { w: SYM_WDTH, h: SYM_HGHT };
